import type {
  CalibrationMatrixGradeConfig,
  CalibrationMatrixWindows,
  ConsultantGrade,
  Rating,
} from '@/api/types/calibrationMatrix.types';

export type WindowTier = 'formalElectcomm' | 'electcomm' | 'promotion';

export const RATINGS_ORDER: Rating[] = ['O', 'AT', 'OT', 'BT'];

export interface BillingGradeGroup {
  billingGrade: string;
  rowCount: number;
  inRegionRatingsByColumn: Map<string, Rating[]>;
}

function billingGradeSuffix(billingGrade: string): number {
  const match = billingGrade.match(/(\d+)$/);
  return match ? parseInt(match[1], 10) : 0;
}

export function deriveBillingGradeGroups(
  gradeConfig: CalibrationMatrixGradeConfig
): BillingGradeGroup[] {
  const seenBillingGrades = new Set<string>();
  const bandsInOrder = new Map<string, string[]>();
  for (const column of gradeConfig.columns) {
    const map = gradeConfig.ratingsToGrades[column];
    if (!map) continue;
    for (const rating of RATINGS_ORDER) {
      const billingGrade = map[rating];
      if (!billingGrade) continue;
      if (!seenBillingGrades.has(billingGrade)) {
        seenBillingGrades.add(billingGrade);
        const prefix = parseGrade(billingGrade) ?? billingGrade;
        const band = bandsInOrder.get(prefix);
        if (band) {
          band.push(billingGrade);
        } else {
          bandsInOrder.set(prefix, [billingGrade]);
        }
      }
    }
  }

  for (const band of bandsInOrder.values()) {
    band.sort((a, b) => billingGradeSuffix(b) - billingGradeSuffix(a));
  }

  return Array.from(bandsInOrder.values())
    .flat()
    .map((bg) => {
      const inRegionRatingsByColumn = new Map<string, Rating[]>();
      let rowCount = 0;
      for (const column of gradeConfig.columns) {
        const map = gradeConfig.ratingsToGrades[column];
        const ratings = map
          ? RATINGS_ORDER.filter((r) => map[r] === bg)
          : [];
        inRegionRatingsByColumn.set(column, ratings);
        if (ratings.length > rowCount) rowCount = ratings.length;
      }
      return {
        billingGrade: bg,
        rowCount,
        inRegionRatingsByColumn,
      };
    });
}

export interface RatingBadgeStyle {
  bgcolor: string;
  color: string;
}

export function getRatingBadgeStyle(rating: Rating): RatingBadgeStyle {
  switch (rating) {
    case 'BT':
      return { bgcolor: 'rgba(211, 47, 47, 0.30)', color: 'common.black' };
    case 'OT':
      return { bgcolor: 'rgba(25, 118, 210, 0.30)', color: 'common.black' };
    case 'AT':
      return { bgcolor: 'rgba(46, 125, 50, 0.30)', color: 'common.black' };
    case 'O':
      return { bgcolor: 'rgba(156, 39, 176, 0.30)', color: 'common.black' };
  }
}

export function formatGeosForTitle(geos: string[]): string {
  if (geos.length === 0) return 'All';
  if (geos.length <= 3) return geos.join(', ');
  return `${geos.length} selected`;
}

export function parseGrade(billingGrade: string): ConsultantGrade | null {
  if (!billingGrade) return null;
  const match = billingGrade.match(/^(AP|AC|M|C)/);
  return match ? (match[1] as ConsultantGrade) : null;
}

// Precedence per FR-7: formalElectcomm > electcomm > promotion.
export function getCellWindowTier(
  column: string,
  rating: Rating,
  windows: CalibrationMatrixWindows
): WindowTier | null {
  if (windows.formalElectcomm?.some((w) => w.tenure === column && w.rating === rating)) {
    return 'formalElectcomm';
  }
  if (windows.electcomm?.some((w) => w.tenure === column && w.rating === rating)) {
    return 'electcomm';
  }
  if (windows.promotion?.some((w) => w.tenure === column && w.rating === rating)) {
    return 'promotion';
  }
  return null;
}

export function getWindowTierLabel(tier: WindowTier | null): string | null {
  switch (tier) {
    case 'promotion':
      return 'Promotion Window';
    case 'electcomm':
      return 'Electcom Window';
    case 'formalElectcomm':
      return 'Electcom Formal Review';
    default:
      return null;
  }
}
